import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
	Modal,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button
} from '@heroui/react';

/**
 * Context for managing unsaved changes across the app
 */
const UnsavedChangesContext = React.createContext(null);

/**
 * Hook to access unsaved changes functionality
 * @returns {{ setDirty: (dirty: boolean) => void, navigateWithCheck: (path: string) => void }}
 */
export function useUnsavedChangesGuard() {
	const context = React.useContext(UnsavedChangesContext);
	if (!context) {
		// Return no-op functions if used outside provider
		return {
			setDirty: () => {},
			navigateWithCheck: path => {
				window.location.href = path;
			}
		};
	}
	return context;
}

/**
 * Wrapper component that provides unsaved changes protection
 *
 * Usage:
 * <UnsavedChangesGuard>
 *   {({ setDirty, navigateWithCheck }) => (
 *     <YourPageContent
 *       onFormChange={() => setDirty(true)}
 *       onSave={() => setDirty(false)}
 *       onNavigate={navigateWithCheck}
 *     />
 *   )}
 * </UnsavedChangesGuard>
 *
 * Or with hook inside child components:
 * <UnsavedChangesGuard>
 *   <YourPageContent />
 * </UnsavedChangesGuard>
 *
 * Then in YourPageContent:
 * const { setDirty, navigateWithCheck } = useUnsavedChangesGuard();
 */
export function UnsavedChangesGuard({ children }) {
	const navigate = useNavigate();
	const [isDirty, setIsDirty] = React.useState(false);
	const [showModal, setShowModal] = React.useState(false);
	const [pendingNavigation, setPendingNavigation] = React.useState(null);

	// Browser beforeunload warning
	React.useEffect(() => {
		const handleBeforeUnload = e => {
			if (isDirty) {
				e.preventDefault();
				e.returnValue = '';
			}
		};
		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => window.removeEventListener('beforeunload', handleBeforeUnload);
	}, [isDirty]);

	// Set dirty state
	const setDirty = React.useCallback(dirty => {
		setIsDirty(dirty);
	}, []);

	// Navigate with check
	const navigateWithCheck = React.useCallback(
		path => {
			if (isDirty) {
				setPendingNavigation(path);
				setShowModal(true);
			} else {
				navigate(path);
			}
		},
		[isDirty, navigate]
	);

	// Modal handlers
	const handleStay = () => {
		setShowModal(false);
		setPendingNavigation(null);
	};

	const handleLeave = () => {
		setShowModal(false);
		setIsDirty(false);
		if (pendingNavigation) {
			navigate(pendingNavigation);
		}
	};

	const contextValue = React.useMemo(
		() => ({ setDirty, navigateWithCheck, isDirty }),
		[setDirty, navigateWithCheck, isDirty]
	);

	return (
		<UnsavedChangesContext.Provider value={contextValue}>
			{typeof children === 'function'
				? children({ setDirty, navigateWithCheck, isDirty })
				: children}

			<Modal scrollBehavior="inside" isOpen={showModal} onClose={handleStay}>
				<ModalContent>
					<ModalHeader>Unsaved Changes</ModalHeader>
					<ModalBody>
						<p>You have unsaved changes. Are you sure you want to leave this page?</p>
					</ModalBody>
					<ModalFooter>
						<Button variant="light" onPress={handleStay}>
							Stay
						</Button>
						<Button color="danger" onPress={handleLeave}>
							Leave
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</UnsavedChangesContext.Provider>
	);
}

export default UnsavedChangesGuard;
